import React, { useEffect, useState } from 'react';
import {
  FreeBonusesContainer,
  LoadingDotsWrapper,
  TextBlock,
  LegalText,
  FreeBonusesFormWrapper,
  FreeBonusesFormContainer,
  SubmitWrapper,
} from './FreeBonusForm.styles';
import Heading from 'app_previous/elements/typography/heading';
import Paragraph from 'app_previous/elements/typography/paragraph';
import {
  addTrackHubSpotFormHandling,
  createHubSpotForm,
  prefillField,
  removeTrackHubSpotFormHandling,
  submitHubSpotFormBuilder,
} from 'src/functions/hubspot/hubspot.service';
import { gTagEvent } from 'src/functions/googleAnalyticsHelper';
import FadeInAnimationWrapper from 'app_previous/elements/animation/fade-in/FadeInAnimationWrapper';
import LoadingDots from '../../LoadingDots/LoadingDots';
import { palette } from 'app_previous/styles/colors';
import { setLocalStorage } from 'src/functions/localStorageHandler';
import { useNameGeneratorHubspotForm } from 'src/hooks/useNameGeneratorHubspotForm';
import Button from 'atoms/Button/Button';

interface FreeBonusFormProps {
  title: string;
  namesToShow: string[];
  hubSpotId: string;
  formWrapperId: string;
  backgroundColor?: string;
}

const FreeBonusForm = (props: FreeBonusFormProps): JSX.Element => {
  if (!props.hubSpotId || !props.formWrapperId) {
    return null;
  }

  const { isSubmitted, setIsSubmitted, setUserEmail, userEmail, setUserName, userName } = useNameGeneratorHubspotForm();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Name generator is multiple submits per session as long as name result is different
    const formId = props.hubSpotId;

    createHubSpotForm({
      formContainer: `#${props.formWrapperId}`,
      formId,
      onFormReadyCallback: () => {
        setIsLoading(false);

        // Prefill form position field
        const pagePath = (typeof window !== 'undefined' && window.location.pathname) || '';
        const namesToShow = props.namesToShow.slice(0, 8).join(', '); // Avoid a very long string
        prefillField(props.formWrapperId, 'input[name="latest_generator_submittal_page"]', pagePath);
        prefillField(props.formWrapperId, 'input[name="latest_generator_title"]', props.title);
        prefillField(props.formWrapperId, 'input[name="latest_generator_results"]', namesToShow);
        prefillField(props.formWrapperId, 'input[name="firstname"]', userName);
        prefillField(props.formWrapperId, 'input[name="email"]', userEmail);

        // timeout so we can wait for the animation to finish
        setTimeout(() => {
          // track form usage with gTag
          addTrackHubSpotFormHandling(props.formWrapperId, trackFormInitialized);
        }, 1000);
      },
      onFormSubmitCallback: () => {
        // hide the form so we can show the thank you text.
        setIsSubmitted(true);
        setIsLoading(true);
      },
      onFormSubmittedCallback: (_, data) => {
        // update context, so we can show thank you for both forms
        setIsSubmitted(true);
        setIsLoading(false);

        // Set Data so we can remember it later
        if (data?.submissionValues) {
          setLocalStorage('nameGenerator:userName', data.submissionValues.firstname);
          setLocalStorage('nameGenerator:userEmail', data.submissionValues.email);

          // set user data
          setUserName(data.submissionValues.firstname);
          setUserEmail(data.submissionValues.email);
        }
      },
    });
  }, []);

  useEffect(() => {
    if (props.namesToShow.length > 0) {
      // Names were changed, reset the form
      setIsSubmitted(false);
      const namesToShow = props.namesToShow.slice(0, 8).join(', '); // Avoid a very long string
      prefillField(props.formWrapperId, 'input[name="latest_generator_results"]', namesToShow);
      prefillField(props.formWrapperId, 'input[name="firstname"]', userName);
      prefillField(props.formWrapperId, 'input[name="email"]', userEmail);
    }
  }, [props.namesToShow]);

  const trackFormInitialized = () => {
    gTagEvent('Form_Start', {
      category: 'form',
      label: props.formWrapperId,
    });

    // remove the listener so we send it just once
    removeTrackHubSpotFormHandling(props.formWrapperId, trackFormInitialized);
  };

  const submitHubspotForm = () => {
    // To validate Hubspot prefilled non-hidden fields, we need to focus and blur them before submitting
    (document.querySelector('input[name="firstname"]') as HTMLInputElement).focus();
    (document.querySelector('input[name="email"]') as HTMLInputElement)?.focus();
    (document.querySelector('input[name="email"]') as HTMLInputElement)?.blur();
    return submitHubSpotFormBuilder(`#${props.formWrapperId}`)();
  };

  const renderSubmitButton = () =>
    !isSubmitted ? (
      <SubmitWrapper data-color-mode='dark'>
        <Button
          onClick={submitHubspotForm}
          variant='primary'
          size='lg'
          type='submit'
        >
          Get Your Gifts
        </Button>
      </SubmitWrapper>
    ) : null;

  const renderLoadingDots = () =>
    isLoading ? (
      <LoadingDotsWrapper>
        <LoadingDots
          loading={isLoading}
          background='accentBlue'
          loadingDotColor='white'
        />
      </LoadingDotsWrapper>
    ) : null;

  const renderThankYouSection = () => {
    if (isSubmitted && !isLoading) {
      return (
        <TextBlock>
          <Paragraph
            text={'Your names and gifts are on their way!'}
            weight='light'
            color='white'
            align='center'
            standardSize='1.5'
            className='text-block-paragraph'
          />
        </TextBlock>
      );
    }
    return null;
  };

  const renderLegalText = () =>
    !isSubmitted && !isLoading ? (
      <LegalText>
        <Paragraph
          text={`By signing up you're opting in to emails about your bonuses. You can unsubscribe at any time.`}
          align='center'
          weight='light'
          color={palette.white}
        />
      </LegalText>
    ) : null;

  return (
    <FreeBonusesContainer backgroundColor={props.backgroundColor}>
      <FadeInAnimationWrapper>
        {isSubmitted ? null : (
          <TextBlock>
            <Heading
              text={'FREE BONUSES to inspire your next step'}
              align='center'
              weight='normal'
              color='white'
              standardSize='2.5'
              className='text-block-header'
            />
            <Paragraph
              text={'We’ll send you your names AND 3 helpful gifts that build on it.'}
              weight='light'
              color='white'
              align='center'
              standardSize='1.5'
              className='text-block-paragraph'
            />
          </TextBlock>
        )}
        {renderLoadingDots()}
        {renderThankYouSection()}
        <FreeBonusesFormWrapper showForm={!isSubmitted && !isLoading}>
          <FreeBonusesFormContainer id={props.formWrapperId} />
          {renderSubmitButton()}
        </FreeBonusesFormWrapper>
        {renderLegalText()}
      </FadeInAnimationWrapper>
    </FreeBonusesContainer>
  );
};

export default FreeBonusForm;
